import { Table } from "@circleco/compass/components/Table";
import type { JSX } from "react";
import { createContext, useContext } from "react";
import type { ExtraProps } from "react-markdown";

type Align = "left" | "center" | "right";
type HastNode = NonNullable<ExtraProps["node"]>;
type HastChild = HastNode["children"][number];

const TableSectionContext = createContext<"header" | "body">("body");
TableSectionContext.displayName = "TableSectionContext";

const isElement = (
  child: HastChild,
): child is Extract<HastChild, { type: "element" }> => child.type === "element";

const findChildElement = (
  parent: HastNode | undefined,
  tagName: string,
): HastNode | undefined =>
  parent?.children.find(
    (child): child is HastNode => isElement(child) && child.tagName === tagName,
  );

const childrenWithTag = (
  parent: HastNode | undefined,
  tagName: string,
): HastNode[] =>
  parent?.children.filter(
    (child): child is HastNode => isElement(child) && child.tagName === tagName,
  ) ?? [];

const CELL_PADDING_PX = 32;
const CHAR_PX = 8;
const MONOSPACE_CHAR_PX = 9;
const MAX_COLUMN_MIN_PX = 160;

const getTextLength = (node: HastChild): number => {
  if (node.type === "text") return node.value.length;
  if (isElement(node)) {
    return node.children.reduce((acc, c) => acc + getTextLength(c), 0);
  }
  return 0;
};

const estimateTextPx = (node: HastChild): number => {
  if (node.type === "text") return node.value.length * CHAR_PX;
  if (!isElement(node)) return 0;
  if (node.tagName === "code") {
    return getTextLength(node) * MONOSPACE_CHAR_PX;
  }
  return node.children.reduce((acc, c) => acc + estimateTextPx(c), 0);
};

const estimateCellMinPx = (cell: HastNode): number =>
  CELL_PADDING_PX +
  cell.children.reduce((acc, c) => acc + estimateTextPx(c), 0);

const computeColumnTemplate = (
  tableNode: HastNode | undefined,
): string | undefined => {
  const headerRow = findChildElement(
    findChildElement(tableNode, "thead"),
    "tr",
  );
  if (!headerRow) return undefined;
  const headerCells = childrenWithTag(headerRow, "th");
  if (headerCells.length === 0) return undefined;

  const cols = headerCells.map(cell => ({
    minPx: estimateCellMinPx(cell),
    weight: Math.max(getTextLength(cell), 1),
  }));

  const tbody = findChildElement(tableNode, "tbody");
  for (const row of childrenWithTag(tbody, "tr")) {
    const cells = childrenWithTag(row, "td");
    cells.forEach((cell, i) => {
      if (i < cols.length) {
        cols[i].minPx = Math.max(cols[i].minPx, estimateCellMinPx(cell));
        cols[i].weight = Math.max(cols[i].weight, getTextLength(cell));
      }
    });
  }

  return cols
    .map(
      c =>
        `minmax(${Math.round(Math.min(c.minPx, MAX_COLUMN_MIN_PX))}px, ${c.weight}fr)`,
    )
    .join(" ");
};

const toAlign = (value: unknown): Align | undefined => {
  if (value === "left" || value === "center" || value === "right") return value;
  return undefined;
};

const MarkdownTableRoot = ({
  node,
  children,
}: JSX.IntrinsicElements["table"] & ExtraProps) => {
  const columns = computeColumnTemplate(node);
  return (
    <div className="py-3">
      <Table.Root {...(columns && { columns })}>{children}</Table.Root>
    </div>
  );
};

const MarkdownTableHead = ({ children }: JSX.IntrinsicElements["thead"]) => (
  <TableSectionContext.Provider value="header">
    <Table.Header>{children}</Table.Header>
  </TableSectionContext.Provider>
);

const MarkdownTableBody = ({ children }: JSX.IntrinsicElements["tbody"]) => (
  <TableSectionContext.Provider value="body">
    <Table.Body>{children}</Table.Body>
  </TableSectionContext.Provider>
);

const MarkdownTableRow = ({ children }: JSX.IntrinsicElements["tr"]) => {
  const section = useContext(TableSectionContext);
  if (section === "header") {
    return <Table.HeaderRow>{children}</Table.HeaderRow>;
  }
  return <Table.Row size="auto">{children}</Table.Row>;
};

const MarkdownTableHeaderCell = ({
  node,
  children,
}: JSX.IntrinsicElements["th"] & ExtraProps) => {
  const align = toAlign(node?.properties?.align);
  return (
    <Table.HeaderCell {...(align && { align })}>
      <span>{children}</span>
    </Table.HeaderCell>
  );
};

const MarkdownTableDataCell = ({
  node,
  children,
}: JSX.IntrinsicElements["td"] & ExtraProps) => {
  const align = toAlign(node?.properties?.align);
  return (
    <Table.BaseCell {...(align && { align })}>
      <span>{children}</span>
    </Table.BaseCell>
  );
};

export const markdownTableComponents = {
  table: MarkdownTableRoot,
  thead: MarkdownTableHead,
  tbody: MarkdownTableBody,
  tr: MarkdownTableRow,
  th: MarkdownTableHeaderCell,
  td: MarkdownTableDataCell,
};
